import * as Sentry from "@sentry/react";
import posthog from "posthog-js";

import { env } from "@/config/env";

import { getMode, MODE } from "./env-mode";

/**
 * Sentry Error Tracking Utility
 *
 * Handles error tracking and monitoring across different environments:
 * - Development: Disabled (errors only logged to console)
 * - Staging: Tracks to Sentry with debug enabled and 100% sampling
 * - Production: Tracks to Sentry with optimized sampling rates
 *
 * Environment detection uses Vite's MODE (development, staging, production, test).
 */

const isDevelopment = () => {
  const mode = getMode();
  return mode === MODE.DEVELOPMENT || mode === MODE.TEST;
};

const isStaging = () => getMode() === MODE.STAGING;

/**
 * Initialize Sentry with appropriate configuration based on environment
 *
 * @example
 * // Called once at app startup in main.tsx
 * initSentry();
 */
export function initSentry() {
  if (isDevelopment()) {
    // Development: Don't initialize Sentry, errors logged naturally
    console.log(
      "[Sentry] Running in development mode - error tracking disabled",
    );
    return;
  }

  // Use the DSN from the environment-specific .env file
  const dsn = env.VITE_SENTRY_DSN;

  if (!dsn) {
    console.error(
      "[Sentry] Missing DSN for environment:",
      getMode(),
      "- Ensure VITE_SENTRY_DSN is defined in your .env file",
    );
    return;
  }

  // Initialize Sentry with environment-specific configuration
  Sentry.init({
    dsn,
    environment: getMode(),

    // Session replay is armed here (eagerly) so startup errors during the
    // 0-3s feature-flag wait still get replay context. When the PostHog flag
    // wins, `disableSentrySessionReplay` stops it and PostHog takes over.
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: true,
      }),

      // posthog-js reads the session replay URL lazily at event emission,
      // so registering this before initPostHog() runs in main.tsx is safe.
      posthog.sentryIntegration({ severityAllowList: ["error", "warning"] }),
    ],

    // Performance Monitoring
    // Staging: Track all transactions for testing
    // Production: Track 10% to conserve quota
    tracesSampleRate: isStaging() ? 1.0 : 0.1,

    replaysSessionSampleRate: isStaging() ? 0.1 : 0.01,
    replaysOnErrorSampleRate: 1.0,

    // Filter out sensitive data and noise
    beforeSend(event, hint) {
      // Don't send errors in development (double check)
      if (isDevelopment()) {
        return null;
      }

      // Filter out specific errors that are noise
      const error = hint.originalException;
      if (error instanceof Error) {
        // Ignore ResizeObserver errors (common browser noise)
        if (error.message.includes("ResizeObserver")) {
          return null;
        }

        // Ignore chunk loading errors (network issues, not app bugs)
        if (error.message.includes("Loading chunk")) {
          return null;
        }
      }

      return event;
    },

    // Enable debug logging in staging
    debug: isStaging(),

    // Additional options
    attachStacktrace: true,
    normalizeDepth: 6,
  });

  // Set global tags
  Sentry.setTag("project", "lotus");

  console.log(`[Sentry] Initialized for ${getMode()} environment`);
}

/**
 * Manually capture an exception in Sentry
 *
 * Most errors are captured automatically, but use this for:
 * - Caught exceptions you want to track
 * - Adding additional context to errors
 *
 * @param error - The error to capture
 * @param context - Additional context about the error
 *
 * @example
 * try {
 *   await riskyOperation();
 * } catch (error) {
 *   captureException(error, {
 *     operation: "riskyOperation",
 *     userId: user.id,
 *   });
 * }
 */
export function captureException(
  error: Error,
  context?: Record<string, unknown>,
) {
  if (isDevelopment()) {
    console.error("[Sentry] Capture Exception:", error, context);
    return;
  }

  if (context) {
    Sentry.withScope((scope) => {
      Object.entries(context).forEach(([key, value]) => {
        scope.setContext(key, value as Record<string, unknown>);
      });
      Sentry.captureException(error);
    });
  } else {
    Sentry.captureException(error);
  }
}

/**
 * Capture a message in Sentry
 *
 * Use for logging important events or warnings that aren't errors
 *
 * @param message - The message to log
 * @param level - Severity level (default: "info")
 *
 * @example
 * captureMessage("User completed onboarding", "info");
 * captureMessage("API rate limit approaching", "warning");
 */
export function captureMessage(
  message: string,
  level: Sentry.SeverityLevel = "info",
) {
  if (isDevelopment()) {
    console.log(`[Sentry] Capture Message (${level}):`, message);
    return;
  }

  Sentry.captureMessage(message, level);
}

/**
 * Set user context in Sentry
 *
 * Call this after user authentication to attach user info to all future events
 *
 * @param user - User information
 *
 * @example
 * setUser({
 *   id: "user-123",
 *   email: "user@example.com",
 *   username: "john_doe",
 * });
 */
export function setUser(user: {
  id: string;
  email?: string;
  username?: string;
  [key: string]: unknown;
}) {
  if (isDevelopment()) {
    console.log("[Sentry] Set User:", user);
    return;
  }

  Sentry.setUser(user);
}

/**
 * Clear user context from Sentry
 *
 * Call this on logout to remove user info from future events
 *
 * @example
 * // On user logout
 * clearUser();
 */
export function clearUser() {
  if (isDevelopment()) {
    console.log("[Sentry] Clear User");
    return;
  }

  Sentry.setUser(null);
}

/**
 * Add breadcrumb to track user actions leading up to errors
 *
 * Breadcrumbs provide context about what the user was doing before an error
 *
 * @param breadcrumb - Breadcrumb data
 *
 * @example
 * addBreadcrumb({
 *   category: "navigation",
 *   message: "User navigated to dashboard",
 *   level: "info",
 * });
 */
export function addBreadcrumb(breadcrumb: {
  category?: string;
  message: string;
  level?: Sentry.SeverityLevel;
  data?: Record<string, unknown>;
}) {
  if (isDevelopment()) {
    console.log("[Sentry] Add Breadcrumb:", breadcrumb);
    return;
  }

  Sentry.addBreadcrumb(breadcrumb);
}

/**
 * Set custom tag for filtering in Sentry
 *
 * @param key - Tag key
 * @param value - Tag value
 *
 * @example
 * setTag("feature", "coaching-session");
 * setTag("payment_plan", "premium");
 */
export function setTag(key: string, value: string) {
  if (isDevelopment()) {
    console.log(`[Sentry] Set Tag: ${key}=${value}`);
    return;
  }

  Sentry.setTag(key, value);
}

/**
 * Set custom context for additional debugging info
 *
 * @param name - Context name
 * @param context - Context data
 *
 * @example
 * setContext("workout", {
 *   exercise_count: 5,
 *   duration: 3600,
 *   intensity: "high",
 * });
 */
export function setContext(name: string, context: Record<string, unknown>) {
  if (isDevelopment()) {
    console.log(`[Sentry] Set Context (${name}):`, context);
    return;
  }

  Sentry.setContext(name, context);
}

/**
 * Wrap async function with error boundary
 *
 * Automatically captures and reports errors from async operations
 *
 * @param fn - Async function to wrap
 * @returns Wrapped function that captures errors
 *
 * @example
 * const safeApiCall = withErrorBoundary(async () => {
 *   const data = await fetchData();
 *   return data;
 * });
 */
export function withErrorBoundary<T extends (...args: unknown[]) => unknown>(
  fn: T,
): T {
  if (isDevelopment()) {
    return fn;
  }

  return ((...args: unknown[]) => {
    try {
      const result = fn(...args);
      if (result instanceof Promise) {
        return result.catch((error) => {
          captureException(error);
          throw error;
        });
      }
      return result;
    } catch (error) {
      if (error instanceof Error) {
        captureException(error);
      }
      throw error;
    }
  }) as T;
}

/**
 * Stop Sentry Session Replay at runtime.
 *
 * Called by the session-replay orchestrator when the PostHog vendor flag wins,
 * so PostHog and Sentry never record in parallel. Sentry replay is armed
 * eagerly in `initSentry`, so this is the handoff point. Idempotent: silently
 * no-ops if Sentry replay was never armed (dev, missing DSN) or already stopped.
 *
 * Returns a promise that resolves when the replay flush/stop completes, so the
 * caller can sequence PostHog start after the Sentry recorder is fully shut down.
 */
export async function disableSentrySessionReplay(): Promise<void> {
  if (isDevelopment()) {
    return;
  }

  const replay = Sentry.getReplay();
  if (!replay) {
    return;
  }

  await replay.stop();
  console.log("[Sentry] Session replay stopped");
}

// Re-export Sentry's ErrorBoundary for React components
export const ErrorBoundary = Sentry.ErrorBoundary;

// Re-export Sentry's withErrorBoundary HOC for React components
export const withSentryErrorBoundary = Sentry.withErrorBoundary;
